import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {PartnerService} from '../../../shared/services/partner.service';
import {UniversityService} from '../../../shared/services/university.service';
import {PartnerPopularCourseService} from '../../../shared/services/partner-popular-course.service';
import {PopularCourseService} from '../../../shared/services/popular-course.service';
import {AlertService} from '../../../shared/services/alert.service';

@Component({
    selector: 'app-partner-courses',
    templateUrl: './partner-courses.component.html',
    styleUrls: ['./partner-courses.component.scss']
})
export class PartnerCoursesComponent implements OnInit {
    partnerSlug: any;
    partnerId: any;
    submitting: any = false;

    // Courses linked to the partner directly from the shared popular_courses catalog — not tied
    // to any specific university.
    showLinkPanel = false;
    searchForm: FormGroup;
    searching = false;
    linkedCourses = [];
    catalogCourses = [];
    selectedIds = new Set<number>();

    // Real PCB-mapped courses belonging to this partner's linked universities (read-only here —
    // managed on each university's own Courses/PCB Mapping screen). A partner has many
    // universities, each with many of its own courses.
    universityCourses = [];

    // `linkedCourses` and `universityCourses` merged into one flat list for a single combined
    // table. Only rows with `removable: true` (the partner's own linked courses) get an Unlink button.
    allCourses = [];

    constructor(private activatedRoute: ActivatedRoute,
                private fb: FormBuilder,
                private partnerService: PartnerService,
                private universityService: UniversityService,
                private partnerPopularCourseService: PartnerPopularCourseService,
                private popularCourseService: PopularCourseService,
                private alertService: AlertService) {
    }

    ngOnInit(): void {
        this.searchForm = this.fb.group({
            'course': ['']
        });
        this.partnerSlug = this.activatedRoute.snapshot.parent.params['slug'];
        this.partnerService.detail(this.partnerSlug).subscribe(data => {
            if (data && data['id']) {
                this.partnerId = data['id'];
                this.getLinkedCourses();
                this.getUniversityCourses();
            }
        });
    }

    getLinkedCourses(): void {
        this.submitting = true;
        this.partnerPopularCourseService.get(this.partnerId).subscribe(data => {
            this.linkedCourses = data || [];
            this.mergeCourses();
            this.submitting = false;
        }, error => {
            this.submitting = false;
            this.alertService.showErrors('Could not refresh linked courses, please reload the page.');
        });
    }

    getUniversityCourses(): void {
        this.universityCourses = [];
        this.universityService.get({'partner_id': this.partnerId, 'page': -1}).subscribe(data => {
            const universities = (data && data['data']) ? data['data'] : [];
            universities.forEach(university => {
                (university.courses || []).forEach(course => {
                    this.universityCourses.push({university, course});
                });
            });
            this.mergeCourses();
        });
    }

    private mergeCourses(): void {
        const ownRows = this.linkedCourses.map(course => ({
            id: course.id,
            name: course.course,
            shortName: course.short_name,
            university: null,
            removable: true,
            source: course
        }));
        const universityRows = this.universityCourses.map(row => ({
            id: null,
            name: row.course?.name,
            shortName: row.course?.short_name,
            university: row.university?.name,
            removable: false,
            source: row
        }));
        this.allCourses = [...ownRows, ...universityRows];
    }

    toggleLinkPanel(): void {
        this.showLinkPanel = !this.showLinkPanel;
        this.selectedIds.clear();
        this.searchForm.patchValue({'course': ''});
        if (this.showLinkPanel) {
            this.searchCatalog();
        } else {
            this.catalogCourses = [];
        }
    }

    searchCatalog(): void {
        const term = (this.searchForm.value && this.searchForm.value.course) ? this.searchForm.value.course : '';
        this.searching = true;
        this.popularCourseService.get({'course': term, 'page': -1}).subscribe(data => {
            const results = (data && data['data']) ? data['data'] : [];
            const linkedIds = new Set(this.linkedCourses.map(c => c.id));
            results.forEach(c => {
                if (linkedIds.has(c.id)) {
                    this.selectedIds.add(c.id);
                }
            });
            this.catalogCourses = results;
            this.searching = false;
        }, () => {
            this.searching = false;
        });
    }

    toggleSelected(course): void {
        if (this.selectedIds.has(course.id)) {
            this.selectedIds.delete(course.id);
        } else {
            this.selectedIds.add(course.id);
        }
    }

    isSelected(course): boolean {
        return this.selectedIds.has(course.id);
    }

    linkSelected(): void {
        if (this.selectedIds.size === 0) {
            return;
        }
        this.submitting = true;
        this.partnerPopularCourseService.link(this.partnerId, Array.from(this.selectedIds)).subscribe(() => {
            this.submitting = false;
            this.alertService.showSuccess({title: 'Success', message: 'Courses linked successfully'});
            this.toggleLinkPanel();
            this.getLinkedCourses();
        }, () => {
            this.submitting = false;
        });
    }

    unlink(course): void {
        this.submitting = true;
        this.partnerPopularCourseService.unlink(this.partnerId, course.id).subscribe(() => {
            this.submitting = false;
            this.getLinkedCourses();
        }, () => {
            this.submitting = false;
        });
    }
}
